/**
 * PIITestModal - Test PII scanner and preview event scanning
 */
import React, { useState } from 'react';
import { Modal, Tabs, Input, Button, Table, Tag, Alert, Spin, Select, Statistic, Row, Col } from 'antd';
import { ExperimentOutlined, EyeOutlined, CheckCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { testPIIScan, previewEventsScan } from '../api/trustgridApi';
import { showError } from '../../../utils/notifications';

const { TextArea } = Input;
const { Option } = Select;

function PIITestModal({ open, onClose, projectId, config }) {
  const [activeTab, setActiveTab] = useState('1');

  // Test Scanner Tab
  const [testText, setTestText] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Preview Events Tab
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewResult, setPreviewResult] = useState(null);
  const [previewSampleSize, setPreviewSampleSize] = useState(10);
  const [previewProvider, setPreviewProvider] = useState(null);

  const handleTestScan = async () => {
    if (!testText.trim()) {
      showError('Please enter some text to scan');
      return;
    }

    setTestLoading(true);
    try {
      const response = await testPIIScan(projectId, testText);
      setTestResult(response.data);
    } catch (error) {
      console.error('Test scan failed:', error);
      showError(error);
    } finally {
      setTestLoading(false);
    }
  };

  const handlePreviewEvents = async () => {
    setPreviewLoading(true);
    try {
      const response = await previewEventsScan(projectId, previewSampleSize, previewProvider);
      setPreviewResult(response.data);
    } catch (error) {
      console.error('Preview scan failed:', error);
      showError(error);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Sample text for testing
  const loadSampleText = () => {
    const sample = `Dear Customer,

Your account information has been updated. Here are your details:

Name: John Smith
Email: john.smith@example.com
Phone: (555) 123-4567
SSN: 123-45-6789
Address: 123 Main Street, Anytown, CA 90210

Your new API key is: sk-abc123def456ghi789jkl012mno345pqr

If you have questions, contact us at support@company.com or call 1-800-555-0100.`;

    setTestText(sample);
  };

  const testMatchesColumns = [
    {
      title: 'Type',
      dataIndex: 'pii_type',
      key: 'pii_type',
      width: 150,
      render: (type) => <Tag color="blue">{type}</Tag>
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      render: (value) => <code style={{ fontSize: '12px' }}>{value}</code>
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 120,
      render: (conf) => `${conf}%`
    }
  ];

  const previewColumns = [
    {
      title: 'Event ID',
      dataIndex: 'event_id',
      key: 'event_id',
      width: 100
    },
    {
      title: 'Provider',
      dataIndex: 'provider',
      key: 'provider',
      width: 100
    },
    {
      title: 'Current',
      key: 'current',
      width: 150,
      render: (_, record) => (
        <div>
          {record.current_pii_detected ? (
            <Tag color="orange">PII Detected</Tag>
          ) : (
            <Tag>No PII</Tag>
          )}
          {record.current_pii_types && record.current_pii_types.length > 0 && (
            <div style={{ fontSize: '11px', marginTop: '4px' }}>
              {record.current_pii_types.join(', ')}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Preview (With Current Config)',
      key: 'preview',
      width: 150,
      render: (_, record) => (
        <div>
          {record.preview_pii_detected ? (
            <Tag color="red">PII Detected</Tag>
          ) : (
            <Tag color="green">No PII</Tag>
          )}
          {record.preview_pii_types && record.preview_pii_types.length > 0 && (
            <div style={{ fontSize: '11px', marginTop: '4px' }}>
              {record.preview_pii_types.join(', ')}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'Confidence',
      dataIndex: 'confidence',
      key: 'confidence',
      width: 100,
      render: (conf) => conf ? `${conf.toFixed(1)}%` : '-'
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      render: (note) => note && <span style={{ fontSize: '11px', color: '#888' }}>{note}</span>
    }
  ];

  const tabItems = [
    {
      key: '1',
      label: (
        <span>
          <ExperimentOutlined /> Test Scanner
        </span>
      ),
      children: (
        <div>
          <Alert
            type="info"
            message="Test PII Detection"
            description="Paste sample text below to see what PII would be detected with your current configuration."
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <div style={{ marginBottom: '12px' }}>
            <Button size="small" onClick={loadSampleText} style={{ marginBottom: '8px' }}>
              Load Sample Text
            </Button>
            <TextArea
              rows={8}
              placeholder="Paste text to scan for PII..."
              value={testText}
              onChange={(e) => setTestText(e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: '13px' }}
            />
          </div>

          <Button
            type="primary"
            icon={<ExperimentOutlined />}
            onClick={handleTestScan}
            loading={testLoading}
            block
          >
            Scan Text
          </Button>

          {testResult && (
            <div style={{ marginTop: '24px' }}>
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col span={8}>
                  <Statistic
                    title="PII Detected"
                    value={testResult.pii_detected ? 'Yes' : 'No'}
                    valueStyle={{ color: testResult.pii_detected ? '#cf1322' : '#3f8600' }}
                    prefix={testResult.pii_detected ? <WarningOutlined /> : <CheckCircleOutlined />}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Types Found"
                    value={testResult.pii_types?.length || 0}
                  />
                </Col>
                <Col span={8}>
                  <Statistic
                    title="Avg Confidence"
                    value={testResult.confidence?.toFixed(1) || 0}
                    suffix="%"
                  />
                </Col>
              </Row>

              {testResult.pii_types && testResult.pii_types.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <strong>Detected Types:</strong>
                  <div style={{ marginTop: '8px' }}>
                    {testResult.pii_types.map(type => (
                      <Tag key={type} color="blue" style={{ marginBottom: '4px' }}>
                        {type}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}

              {testResult.matches && testResult.matches.length > 0 && (
                <>
                  <strong>Matches Found ({testResult.match_count}):</strong>
                  <Table
                    dataSource={testResult.matches}
                    columns={testMatchesColumns}
                    rowKey={(_, idx) => idx}
                    pagination={false}
                    size="small"
                    style={{ marginTop: '8px' }}
                  />
                </>
              )}

              <Alert
                type="success"
                message="Configuration Applied"
                description={
                  <div style={{ fontSize: '12px' }}>
                    <div>Enabled Types: {testResult.config_applied?.pii_types_enabled?.length || 'All'}</div>
                    <div>Confidence Threshold: {testResult.config_applied?.confidence_threshold || 80}%</div>
                  </div>
                }
                showIcon
                style={{ marginTop: '16px' }}
              />
            </div>
          )}
        </div>
      )
    },
    {
      key: '2',
      label: (
        <span>
          <EyeOutlined /> Preview Events
        </span>
      ),
      children: (
        <div>
          <Alert
            type="info"
            message="Preview Event Scanning"
            description="Scan a sample of your recent events to see what PII would be detected with your current configuration."
            showIcon
            style={{ marginBottom: '16px' }}
          />

          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <Select
              placeholder="Sample size"
              value={previewSampleSize}
              onChange={setPreviewSampleSize}
              style={{ width: 150 }}
            >
              <Option value={5}>5 events</Option>
              <Option value={10}>10 events</Option>
              <Option value={20}>20 events</Option>
              <Option value={50}>50 events</Option>
            </Select>

            <Select
              placeholder="Filter by provider"
              value={previewProvider}
              onChange={setPreviewProvider}
              allowClear
              style={{ width: 200 }}
            >
              <Option value="openai">OpenAI</Option>
              <Option value="anthropic">Anthropic</Option>
              <Option value="bedrock">AWS Bedrock</Option>
              <Option value="github">GitHub</Option>
              <Option value="slack">Slack</Option>
              <Option value="datadog">Datadog</Option>
            </Select>

            <Button
              type="primary"
              icon={<EyeOutlined />}
              onClick={handlePreviewEvents}
              loading={previewLoading}
              style={{ flex: 1 }}
            >
              Preview Scan
            </Button>
          </div>

          {previewResult && (
            <div style={{ marginTop: '24px' }}>
              <Row gutter={16} style={{ marginBottom: '16px' }}>
                <Col span={12}>
                  <Statistic
                    title="Events Sampled"
                    value={previewResult.sample_size}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Scanning Mode"
                    value={previewResult.config?.pii_scanning_mode || 'none'}
                    valueStyle={{ fontSize: '16px', textTransform: 'uppercase' }}
                  />
                </Col>
              </Row>

              <Table
                dataSource={previewResult.results}
                columns={previewColumns}
                rowKey="event_id"
                pagination={false}
                scroll={{ y: 400 }}
                size="small"
              />

              <Alert
                type="warning"
                message="Note"
                description="This is a preview only. No events will be modified. Changes will take effect after you save the configuration and run a batch re-scan or wait for new events."
                showIcon
                style={{ marginTop: '16px' }}
              />
            </div>
          )}
        </div>
      )
    }
  ];

  return (
    <Modal
      title="🧪 Test & Preview PII Detection"
      open={open}
      onCancel={onClose}
      width={1000}
      footer={[
        <button key="close" className="custom-button" onClick={onClose}>
          Close
        </button>
      ]}
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </Modal>
  );
}

export default PIITestModal;
